import convert from 'convert-units';
import type { ReactElement } from 'react';
import React, { useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { AppContext } from '../../../context/app';
import useIsMobile from '../../../hooks/useIsMobile';
import { useNavigationManager } from '../../../hooks/useNavigationManager';
import { useNetworkManager } from '../../../hooks/useNetworkManager';
import ButtonStyled from '../button/Button';
import { DirectionsList } from '../directionsList/DirectionsList';
import Icon from '../icon/Icon';
import { IconType } from '../icon/types';
import styles from './azureDirectionsList.module.scss';

interface IconElementProps {
  name: string;
}
const IconElementComponent: React.FC<IconElementProps> = (props) => (
  <span className="material-icons">{props.name}</span>
);

interface AzureDirectionsListProps {
  collapsed?: boolean;
  printMode?: boolean;
}
const AzureDirectionsList: React.FC<AzureDirectionsListProps> = ({
  collapsed = false,
  printMode,
}) => {
  const { availableSites, directions, setLegToggle } = useContext(AppContext);
  const { arrivalGeoNode, legTwoRoute } = useNavigationManager();
  const { siteName } = useNetworkManager();

  const { t } = useTranslation('common');
  const isMobile = useIsMobile();
  const handleLegToggle = (e: boolean) => {
    if (setLegToggle) {
      setLegToggle(e);
    }
  };

  const getManeuverIcon = (maneuver: string): ReactElement => {
    const normalizedManeuver = maneuver.toLowerCase().replace('_', '-');

    if (['turn-slight-left', 'bear-left'].includes(normalizedManeuver)) {
      return <IconElementComponent name="keyboard_double_arrow_left" />;
    }
    if (['turn-sharp-left', 'sharp-left'].includes(normalizedManeuver)) {
      return <IconElementComponent name="call_made" />;
    }
    if (['fork-left', 'keep-left'].includes(normalizedManeuver)) {
      return <IconElementComponent name="fork_left" />;
    }
    if (['turn-slight-right', 'bear-right'].includes(normalizedManeuver)) {
      return <IconElementComponent name="double_arrow" />;
    }
    if (['turn-sharp-right', 'sharp-right'].includes(normalizedManeuver)) {
      return <IconElementComponent name="call_made" />;
    }
    if (['fork-right', 'keep-right'].includes(normalizedManeuver)) {
      return <IconElementComponent name="fork_right" />;
    }
    if (
      ['make-uturn', 'uturn-left', 'uturn-right'].includes(normalizedManeuver)
    ) {
      return <IconElementComponent name="u_turn_left" />;
    }
    if (
      [
        'ramp-right',
        'motorway-exit-right',
        'entrance_ramp',
        'enter_highway',
        'enter_motorway',
        'enter_freeway',
      ].includes(normalizedManeuver)
    ) {
      return <IconElementComponent name="ramp_right" />;
    }
    if (['ramp-left', 'motorway-exit-left'].includes(normalizedManeuver)) {
      return <IconElementComponent name="ramp_left" />;
    }

    switch (normalizedManeuver) {
      case 'turn-sharp-left':
        return <IconElementComponent name="call_made" />;
      case 'uturn-right':
        return <IconElementComponent name="u_turn_right" />;
      case 'turn-slight-right':
        return <IconElementComponent name="double_arrow" />;
      case 'merge':
        return <IconElementComponent name="merge_type" />;
      case 'roundabout-left':
        return <IconElementComponent name="roundabout_left" />;
      case 'roundabout-right':
        return <IconElementComponent name="roundabout_right" />;
      case 'uturn-left':
        return <IconElementComponent name="u_turn_left" />;
      case 'turn-slight-left':
        return <IconElementComponent name="double_arrow" />;
      case 'turn-left':
        return <IconElementComponent name="turn_left" />;
      case 'ramp-right':
        return <IconElementComponent name="ramp_right" />;
      case 'turn-right':
        return <IconElementComponent name="turn_right" />;
      case 'fork-right':
        return <IconElementComponent name="fork_right" />;
      case 'straight':
        return <IconElementComponent name="straight" />;
      case 'fork-left':
        return <IconElementComponent name="fork_left" />;
      case 'ferry-train':
        return <IconElementComponent name="directions_railway_filled" />;
      case 'turn-sharp-right':
        return <IconElementComponent name="call_made" />;
      case 'ramp-left':
        return <IconElementComponent name="ramp_left" />;
      case 'ferry':
        return <IconElementComponent name="directions_boat_filled" />;
      default:
        return <IconElementComponent name="straight" />;
    }
  };

  const steps = useMemo(() => {
    const instructions = directions.guidance?.instructions;
    return instructions?.map((instruction: any, instructionIndex: any) => {
      let stepDistance =
        convert(
          instructions[instructionIndex + 1]?.routeOffsetInMeters -
            instruction.routeOffsetInMeters || undefined,
        )
          .from('m')
          .to('mi') || 0;

      const units = stepDistance < 0.2 ? 'feet' : 'miles';
      if (stepDistance < 0.2) {
        stepDistance = convert(stepDistance).from('mi').to('ft');
      }

      const stepDistanceText =
        stepDistance > 0
          ? `${stepDistance
              .toFixed(units === 'miles' ? 1 : 0)
              .toLocaleString()} ${units}`
          : '';

      return (
        <li key={`${instructionIndex}`} className={styles.listItem}>
          <div className={styles.listItemIcon}>
            {getManeuverIcon(instruction.maneuver)}
          </div>
          <div className={styles.listItemDetails}>
            <span dangerouslySetInnerHTML={{ __html: instruction.message }} />
            <span className={styles.distanceRow}>
              {stepDistanceText}
              {stepDistance > 0 && <div className={styles.hr} />}
            </span>
          </div>
        </li>
      );
    });
  }, [directions]);

  if (!directions) return null;
  if (!isMobile) {
    return (
      <DirectionsList
        legOne
        collapsed={collapsed}
        onClick={handleLegToggle}
        title={t('campusNavigation.directions.step1')}
        whereTo={t('campusNavigation.directions.drivingTo', {
          siteName:
            availableSites?.find((site) => site.id === arrivalGeoNode?.site)
              ?.short_name || siteName,
        })}
        printMode={printMode}
      >
        <>
          <div className={styles.hr} />
          <ul>{steps}</ul>
        </>
      </DirectionsList>
    );
  }
  return (
    <div className={styles.mobileDirectionList}>
      <div className={styles.mobileDirectionRow}>
        <span className={styles.mobileDirectionBold}>
          {t('campusNavigation.directions.step1')}
        </span>
        <span className={styles.mobileDirection}>
          {t('campusNavigation.directions.driveTo', {
            siteName:
              availableSites?.find((site) => site.id === arrivalGeoNode?.site)
                ?.short_name || siteName,
          })}
        </span>
      </div>
      {legTwoRoute && (
        <div className={styles.mobileDirectionButtons}>
          <ButtonStyled onClick={() => {}} disabled={true} flexWide uppercase>
            <Icon icon={IconType.LEFT} />
            {t('campusNavigation.directions.previous')}
          </ButtonStyled>
          <ButtonStyled onClick={() => setLegToggle(true)} flexWide uppercase>
            {t('campusNavigation.directions.continue')}
            <Icon icon={IconType.RIGHT} />
          </ButtonStyled>
        </div>
      )}
    </div>
  );
};

export default React.memo(AzureDirectionsList);
